import React from 'react';
import { Link } from 'react-router-dom';
import {
    Home, Users, Database,
    Bell, HelpCircle, ChevronDown, BarChart2, ArrowLeft,
    Plus, Check, Clock, RefreshCw,
    Facebook, Instagram, Linkedin, File,
    AlertTriangle, Lock, Shield
} from 'lucide-react';

const LumiLogicDataSource = () => {
    return (
        <div className="flex flex-col h-screen bg-gray-100">
            {/* Top Navigation Bar */}
            <header className="bg-gray-800 shadow-sm py-3 px-6 flex justify-between items-center">
                <div className="flex items-center space-x-10">
                    <div className="flex items-center">
                        <img src="/logo192.png" alt="LumiLogic Logo" className="w-auto h-8 mr-2" />
                        <span className="font-bold text-xl text-blue-600">LumiLogic</span>
                    </div>
                    <div className="hidden md:flex space-x-6">
                        <Link to="/" className="px-3 py-1 text-sm font-medium rounded flex items-center text-gray-600 hover:bg-gray-100">
                            <Home size={16} className="mr-2" />
                            Dashboard
                        </Link>
                        <button className="px-3 py-1 text-sm font-medium rounded flex items-center text-blue-700 bg-blue-50">
                            <Database size={16} className="mr-2" />
                            Data Sources
                        </button>
                        <button className="px-3 py-1 text-sm font-medium rounded flex items-center text-gray-600 hover:bg-gray-100">
                            <BarChart2 size={16} className="mr-2" />
                            Reports
                        </button>
                        <button className="px-3 py-1 text-sm font-medium rounded flex items-center text-gray-600 hover:bg-gray-100">
                            <Users size={16} className="mr-2" />
                            Team
                        </button>
                    </div>
                </div>
                <div className="flex items-center space-x-4">
                    <button className="p-2 text-gray-500 hover:bg-gray-100 rounded-full relative">
                        <Bell size={20} />
                        <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                    </button>
                    <button className="p-2 text-gray-500 hover:bg-gray-100 rounded-full">
                        <HelpCircle size={20} />
                    </button>
                    <div className="flex items-center cursor-pointer">
                        <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-medium">
                            KF
                        </div>
                        <ChevronDown size={16} className="ml-2 text-gray-500" />
                    </div>
                </div>
            </header>

            {/* Page Header */}
            <div className="bg-white border-b border-gray-200 py-4 px-6">
                <div className="flex items-center">
                    <Link to="/" className="mr-3 text-gray-500 hover:text-gray-700">
                        <ArrowLeft size={20} />
                    </Link>
                    <h1 className="text-xl font-bold text-gray-800">Data Sources</h1>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex flex-1 overflow-hidden">
                {/* Sidebar */}
                <div className="w-64 bg-white border-r border-gray-200 overflow-y-auto">
                    <div className="p-4">
                        <button className="flex items-center justify-center w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                            <Plus size={16} className="mr-2" />
                            Add New Source
                        </button>
                    </div>
                    <div className="px-4 py-2 border-b border-gray-200">
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Connected Sources</h3>
                    </div>
                    <div className="py-2">
                        <button className="flex items-center px-4 py-3 w-full text-left hover:bg-blue-50 bg-blue-50 border-l-4 border-blue-600">
                            <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center mr-3">
                                <Facebook size={16} className="text-blue-600" />
                            </div>
                            <div>
                                <h4 className="text-sm font-medium text-gray-800">Facebook Insights</h4>
                                <p className="text-xs text-gray-500">Last sync: 2 hours ago</p>
                            </div>
                        </button>
                        <button className="flex items-center px-4 py-3 w-full text-left hover:bg-blue-50">
                            <div className="w-8 h-8 bg-red-100 rounded-md flex items-center justify-center mr-3">
                                <div className="text-red-600 font-bold text-xs">GA4</div>
                            </div>
                            <div>
                                <h4 className="text-sm font-medium text-gray-800">Google Analytics 4</h4>
                                <p className="text-xs text-gray-500">Last sync: 1 hour ago</p>
                            </div>
                        </button>
                        <button className="flex items-center px-4 py-3 w-full text-left hover:bg-blue-50">
                            <div className="w-8 h-8 bg-pink-100 rounded-md flex items-center justify-center mr-3">
                                <Instagram size={16} className="text-pink-600" />
                            </div>
                            <div>
                                <h4 className="text-sm font-medium text-gray-800">Instagram</h4>
                                <p className="text-xs text-gray-500">Last sync: 3 hours ago</p>
                            </div>
                        </button>
                        <button className="flex items-center px-4 py-3 w-full text-left hover:bg-blue-50">
                            <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center mr-3">
                                <Linkedin size={16} className="text-blue-800" />
                            </div>
                            <div>
                                <h4 className="text-sm font-medium text-gray-800">LinkedIn Ads</h4>
                                <p className="text-xs text-gray-500">Last sync: 3 hours ago</p>
                            </div>
                        </button>
                        <button className="flex items-center px-4 py-3 w-full text-left hover:bg-blue-50">
                            <div className="w-8 h-8 bg-green-100 rounded-md flex items-center justify-center mr-3">
                                <File size={16} className="text-green-600" />
                            </div>
                            <div>
                                <h4 className="text-sm font-medium text-gray-800">QuickBooks</h4>
                                <p className="text-xs text-gray-500">Last sync: 6 hours ago</p>
                            </div>
                        </button>
                    </div>
                    <div className="px-4 py-2 border-b border-t border-gray-200">
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Available Sources</h3>
                    </div>
                    <div className="py-2">
                        <button className="flex items-center px-4 py-3 w-full text-left hover:bg-gray-50">
                            <div className="w-8 h-8 bg-gray-100 rounded-md flex items-center justify-center mr-3">
                                <div className="text-xs font-medium text-gray-600">CSV</div>
                            </div>
                            <div>
                                <h4 className="text-sm font-medium text-gray-800">CSV Upload</h4>
                                <p className="text-xs text-gray-500">Connect via file upload</p>
                            </div>
                        </button>
                        <button className="flex items-center px-4 py-3 w-full text-left hover:bg-gray-50">
                            <div className="w-8 h-8 bg-gray-100 rounded-md flex items-center justify-center mr-3">
                                <div className="text-xs font-medium text-gray-600">DB</div>
                            </div>
                            <div>
                                <h4 className="text-sm font-medium text-gray-800">Database</h4>
                                <p className="text-xs text-gray-500">SQL, PostgreSQL, etc.</p>
                            </div>
                        </button>
                    </div>
                </div>

                {/* Main Panel */}
                <div className="flex-1 overflow-y-auto p-6">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        {/* Header with tabs */}
                        <div className="border-b border-gray-200">
                            <div className="px-6 pt-6 pb-4">
                                <div className="flex items-center">
                                    <div className="w-10 h-10 bg-blue-100 rounded-md flex items-center justify-center mr-4">
                                        <Facebook size={20} className="text-blue-600" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-bold text-gray-800">Facebook Insights</h2>
                                        <p className="text-sm text-gray-500">Connected on April 15, 2025</p>
                                    </div>
                                    <span className="ml-4 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Active</span>
                                </div>
                            </div>
                            <div className="px-6 flex border-b border-gray-200">
                                <button className="px-4 py-2 border-b-2 border-blue-600 text-blue-600 font-medium">
                                    Overview
                                </button>
                                <button className="px-4 py-2 text-gray-600 hover:text-gray-800">
                                    Data
                                </button>
                                <button className="px-4 py-2 text-gray-600 hover:text-gray-800">
                                    Settings
                                </button>
                                <button className="px-4 py-2 text-gray-600 hover:text-gray-800">
                                    Alerts
                                </button>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-6">
                            {/* Connection Status */}
                            <div className="mb-8">
                                <h3 className="text-lg font-medium text-gray-800 mb-4">Connection Status</h3>
                                <div className="bg-green-50 border border-green-100 rounded-md p-4 flex items-center">
                                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                                        <Check size={16} className="text-green-600" />
                                    </div>
                                    <div>
                                        <h4 className="font-medium text-green-800">Connection is active and healthy</h4>
                                        <p className="text-sm text-green-700">Last successful sync was 2 hours ago at 10:45 AM</p>
                                    </div>
                                    <button className="ml-auto flex items-center px-3 py-1 bg-white border border-gray-300 rounded-md text-sm text-gray-700">
                                        <RefreshCw size={14} className="mr-2" />
                                        Refresh Now
                                    </button>
                                </div>
                            </div>

                            {/* Sync Settings */}
                            <div className="mb-8">
                                <h3 className="text-lg font-medium text-gray-800 mb-4">Sync Settings</h3>
                                <div className="bg-white border border-gray-200 rounded-md">
                                    <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                                        <div>
                                            <h4 className="font-medium text-gray-800">Sync Frequency</h4>
                                            <p className="text-sm text-gray-500">How often LumiLogic refreshes your data</p>
                                        </div>
                                        <div className="flex items-center">
                                            <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                                                <option>Every 3 hours</option>
                                                <option>Every 6 hours</option>
                                                <option>Daily</option>
                                                <option>Weekly</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                                        <div>
                                            <h4 className="font-medium text-gray-800">Data Retention</h4>
                                            <p className="text-sm text-gray-500">How long to keep historical data</p>
                                        </div>
                                        <div className="flex items-center">
                                            <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                                                <option>Last 12 months</option>
                                                <option>Last 6 months</option>
                                                <option>Last 3 months</option>
                                                <option>All time</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="px-4 py-3 flex justify-between items-center">
                                        <div>
                                            <h4 className="font-medium text-gray-800">Automatic Sync</h4>
                                            <p className="text-sm text-gray-500">Enable or disable automated data syncing</p>
                                        </div>
                                        <div className="flex items-center">
                                            <div className="w-12 h-6 bg-blue-600 rounded-full px-1 flex items-center">
                                                <div className="w-4 h-4 bg-white rounded-full ml-auto"></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Data Elements */}
                            <div className="mb-8">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-medium text-gray-800">Available Data Elements</h3>
                                    <button className="text-sm text-blue-600 font-medium">Select All</button>
                                </div>
                                <div className="bg-white border border-gray-200 rounded-md">
                                    <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                                        <div className="flex items-center">
                                            <div className="w-5 h-5 bg-blue-100 border border-blue-600 rounded flex items-center justify-center mr-3">
                                                <Check size={12} className="text-blue-600" />
                                            </div>
                                            <div>
                                                <h4 className="font-medium text-gray-800">Page Insights</h4>
                                                <p className="text-sm text-gray-500">Page views, likes, followers, reach</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center text-sm text-gray-500">
                                            <Clock size={14} className="mr-1" />
                                            Updated daily
                                        </div>
                                    </div>
                                    <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                                        <div className="flex items-center">
                                            <div className="w-5 h-5 bg-blue-100 border border-blue-600 rounded flex items-center justify-center mr-3">
                                                <Check size={12} className="text-blue-600" />
                                            </div>
                                            <div>
                                                <h4 className="font-medium text-gray-800">Post Performance</h4>
                                                <p className="text-sm text-gray-500">Engagement, reach, comments, shares</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center text-sm text-gray-500">
                                            <Clock size={14} className="mr-1" />
                                            Updated daily
                                        </div>
                                    </div>
                                    <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                                        <div className="flex items-center">
                                            <div className="w-5 h-5 bg-blue-100 border border-blue-600 rounded flex items-center justify-center mr-3">
                                                <Check size={12} className="text-blue-600" />
                                            </div>
                                            <div>
                                                <h4 className="font-medium text-gray-800">Audience Demographics</h4>
                                                <p className="text-sm text-gray-500">Age, gender, location, language</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center text-sm text-gray-500">
                                            <Clock size={14} className="mr-1" />
                                            Updated weekly
                                        </div>
                                    </div>
                                    <div className="px-4 py-3 flex justify-between items-center">
                                        <div className="flex items-center">
                                            <div className="w-5 h-5 border border-gray-300 rounded mr-3"></div>
                                            <div>
                                                <h4 className="font-medium text-gray-800">Ad Performance</h4>
                                                <p className="text-sm text-gray-500">Impressions, clicks, CPC, CTR, conversions</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center text-sm text-gray-500">
                                            <Clock size={14} className="mr-1" />
                                            Updated hourly
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Connection Security */}
                            <div>
                                <h3 className="text-lg font-medium text-gray-800 mb-4">Connection Security</h3>
                                <div className="bg-white border border-gray-200 rounded-md p-4">
                                    <div className="flex items-start mb-4">
                                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mt-1 mr-3">
                                            <Lock size={16} className="text-blue-600" />
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-gray-800">End-to-End Encryption</h4>
                                            <p className="text-sm text-gray-600 mt-1">Your connection is secured with end-to-end encryption. All data is encrypted in transit and at rest.</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start mb-4">
                                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mt-1 mr-3">
                                            <Shield size={16} className="text-blue-600" />
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-gray-800">OAuth 2.0 Authentication</h4>
                                            <p className="text-sm text-gray-600 mt-1">We use OAuth 2.0 for secure authentication without storing your passwords.</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start">
                                        <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center mt-1 mr-3">
                                            <AlertTriangle size={16} className="text-yellow-600" />
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-gray-800">Permission Review Required</h4>
                                            <p className="text-sm text-gray-600 mt-1">Facebook has updated their API policies. Please review and reauthorize permissions by June 1, 2025.</p>
                                            <button className="mt-2 px-3 py-1 bg-blue-600 text-white text-sm rounded-md">Review Permissions</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LumiLogicDataSource;